import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import useNotification from '../../../../../components/_hook/useNotification';
import DialogConfirm from '../../../../../components/_dialog/DialogConfirm';
import StatusIconButton from '../../../../../components/_iconButton/StatusIconButton';
import DeleteIconButton from '../../../../../components/_iconButton/DeleteIconButton';
import useLanguage from '../../../../../components/_hook/useLanguage';
import { useDeleteCompanyProductCustomFieldsMutation } from '../../../../../../graphql/mutation/company/deleteCompanyProductCustomFieldsMutation';
import { ICompanySchema } from '../../../../../../graphql/type/company';
import { IProductCustomFieldsSubTypeInput } from '../../../../../../graphql/type/_inputTypes/productCustomFields';
import FormCreateUpdateProductRamification from './FormCreateUpdateProductRamification';

interface IProps {
  companyId: string;
  item: ICompanySchema['custom_fields'][0];
  onPublish: (item: ICompanySchema['custom_fields'][0]) => void;
  onUnpublish: (item: ICompanySchema['custom_fields'][0]) => void;
  productVariationCustomFields: IProductCustomFieldsSubTypeInput[];
}

export default function ProductVariationRamificationTableItem(props: IProps) {
  const { t } = useTranslation();
  const {
    item,
    companyId,
    onPublish,
    onUnpublish,
    productVariationCustomFields,
  } = props;
  const { notification } = useNotification();
  const { getText } = useLanguage();
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isOpenUpdateDialog, setIsOpenUpdateDialog] = useState(false);

  const [
    deleteCompanyProductCustomFieldsMutation,
    { loading: isDeletingCompanyProductCustomFields },
  ] = useDeleteCompanyProductCustomFieldsMutation(
    `updatedCompany {
				_id
                custom_fields {
                  id
                  locked
                  title {
                    la
                    text
                  }
                  description {
                    la
                    text
                  }
                  field_type
                  data_type
                  attributes_input
                  attributes
                  currency
                }
			}`,
    {
      onError: (e) => notification.error(e.message),
    }
  );

  async function onClickDelete() {
    await deleteCompanyProductCustomFieldsMutation({
      variables: {
        companyId,
        productCustomFieldIds: [item.id],
      },
    });
  }

  const status = Boolean(
    productVariationCustomFields.find(
      (productVariationCustomField) =>
        productVariationCustomField.id === item.id &&
        productVariationCustomField.status
    )
  );

  return (
    <>
      <DialogConfirm
        title={t('are_you_sure_you_want_to_delete')}
        open={isOpenDeleteDialog}
        onClose={() => setIsOpenDeleteDialog(false)}
        onConfirm={onClickDelete}
      />
      <Dialog
        open={isOpenUpdateDialog}
        onClose={() => setIsOpenUpdateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <FormCreateUpdateProductRamification
          ramificationId={item.id}
          onCancel={() => {
            setIsOpenUpdateDialog(false);
          }}
          onSubmit={() => {
            setIsOpenUpdateDialog(false);
          }}
        />
      </Dialog>
      <TableCell>
        <Button
          color="primary"
          onClick={() => {
            setIsOpenUpdateDialog(true);
          }}
        >
          {getText(item.title)}
        </Button>
      </TableCell>
      <TableCell>{item.description && getText(item.description)}</TableCell>
      <TableCell>{t(item.field_type)}</TableCell>
      <TableCell>{t(item.data_type)}</TableCell>
      <TableCell>
        <StatusIconButton
          status={status}
          tooltip={status ? t('publish') : t('unpublish')}
          onClick={status ? () => onUnpublish(item) : () => onPublish(item)}
        />
        <IconButton
          size="small"
          color="primary"
          onClick={() => {
            setIsOpenUpdateDialog(true);
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        {item.locked ? (
          <IconButton size="small" disabled>
            <LockIcon fontSize="small" />
          </IconButton>
        ) : (
          <DeleteIconButton
            loading={isDeletingCompanyProductCustomFields}
            tooltip={t('delete')}
            onClick={() => setIsOpenDeleteDialog(true)}
          />
        )}
      </TableCell>
    </>
  );
}
